import React from 'react';
import PropTypes from 'prop-types';

import { SUBSCRIPTION_TYPES } from '../../../constants/subscriptions';

/**
 * ComparisonTable component displays a side-by-side comparison of features
 * available in standard vs elevated profiles
 *
 * @param {Object} props
 * @param {ReadonlyArray<import('../constants').ComparisonFeature>} props.features - Array of features to compare
 * @param {string} [props.className] - Additional CSS classes
 */
const ComparisonTable = ({ features = [], className = '' }) => {
  return (
    <div className={`flex flex-col md:flex-row rounded-lg ${className}`}>
      {/* Features Column */}
      <div className="flex w-full md:w-55 flex-col items-start rounded-l-lg border border-gray-20 bg-white">
        <div className="flex px-6 py-3 items-center justify-center gap-2.5 self-stretch bg-gray-20 text-gray-90 text-sm font-semibold leading-5">
          FEATURES
        </div>
        <div className="flex px-6 pb-5 flex-col items-start gap-1 self-stretch">
          <div className="flex min-h-[92px] py-5 flex-col justify-center items-start self-stretch w-42 flex-1 text-deep-blue-70 text-sm font-semibold leading-5">
            Price
          </div>
          {features.map((item) => (
            <div
              key={item.feature}
              className="flex h-13 pt-1 flex-col justify-center items-start gap-4 border-t border-gray-20 text-deep-blue-70 text-sm font-semibold leading-5"
            >
              {item.feature}
            </div>
          ))}
        </div>
      </div>

      {/* Standard Column */}
      <div className="flex w-55 flex-col items-start border border-gray-20 bg-white">
        <div className="flex px-6 py-3 items-center justify-center gap-2.5 self-stretch bg-gray-20 text-gray-90 text-sm font-semibold leading-5">
          STANDARD
        </div>
        <div className="flex px-6 pb-5 flex-col items-start gap-1 self-stretch">
          <div className="flex flex-col justify-center items-center w-full min-h-[92px]">
            <h3 className="flex justify-center items-center gap-1 self-stretch text-gray-90 text-xl font-semibold leading-8">
              $0
            </h3>
            <span className="text-gray-90 text-center text-sm font-normal leading-5">
              Your current profile
            </span>
          </div>
          {features.map((item) => (
            <div
              key={item.feature}
              className="flex h-13 pt-1 flex-col justify-center items-center gap-4 self-stretch border-t border-gray-20"
            >
              {item.standard ? (
                <img
                  src="/public/images/check-icon.svg"
                  alt="check-icon"
                  className="w-5 h-5"
                />
              ) : (
                <span className="text-deep-blue-70 text-center text-xl font-light leading-8">
                  -
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Elevated Column */}
      <div className="flex w-78 h-[548px] flex-col items-center rounded-r-lg border border-blue-30 bg-white shadow-elevated">
        <div className="flex px-2 py-3 justify-center items-center gap-2.5 self-stretch bg-gradient-to-r from-blue-50 to-blue-30 text-white text-sm font-semibold leading-5">
          ELEVATED
        </div>
        <div className="flex px-6 pb-5 flex-col items-center gap-1 self-stretch">
          <div className="flex items-center gap-5">
            <div className="flex w-33 py-5 flex-col items-center">
              <div className="flex items-center gap-1">
                <h3 className="text-gray-90 text-xl font-semibold leading-8 mb-0">
                  {SUBSCRIPTION_TYPES.MONTHLY.displayPrice}
                </h3>
                <span className="text-gray-90 text-sm font-normal leading-5 whitespace-nowrap">
                  &nbsp;/ month
                </span>
              </div>
              <span className="text-gray-90 text-sm font-normal leading-5">
                billed monthly
              </span>
            </div>
            <div className="w-px h-15 bg-gray-20"></div>
            <div className="flex py-5 flex-col items-center">
              <div className="flex items-center gap-1">
                <h3 className="text-gray-90 text-xl font-semibold leading-8 mb-0">
                  {SUBSCRIPTION_TYPES.ANNUAL.displayPrice}
                </h3>
                <span className="text-gray-90 text-sm font-normal leading-5 whitespace-nowrap">
                  &nbsp;/ year
                </span>
              </div>
              <span className="text-gray-90 text-sm font-normal leading-5">
                billed annually
              </span>
            </div>
          </div>
          {features.map((item) => (
            <div
              key={item.feature}
              className="flex h-13 pt-1 flex-col justify-center items-center self-stretch gap-4 border-t border-gray-20"
            >
              {item.elevated ? (
                <img
                  src="/public/images/check-icon.svg"
                  alt="check-icon"
                  className="w-5 h-5 text-blue-30"
                />
              ) : (
                <span className="text-deep-blue-70 text-center text-xl font-light leading-8">
                  -
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

ComparisonTable.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      feature: PropTypes.string.isRequired,
      standard: PropTypes.bool.isRequired,
      elevated: PropTypes.bool.isRequired,
    }),
  ),
  className: PropTypes.string,
};

export default ComparisonTable;
